import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import api from '@/lib/api.ts'
import {
  Package,
  CheckCircle2,
  XCircle,
  Printer,
  Search,
  Truck,
} from 'lucide-react'
import {
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  LoadingState,
  PageHeader,
  Select,
  useToast,
} from '@/components/ui'

type DhlProduct = 'V01PAK' | 'V06PAK' | 'V86PARCEL' | 'V01PAKI' | 'V55PAK'

const productLabels: Record<DhlProduct, string> = {
  V01PAK: 'DHL Paket (national)',
  V06PAK: 'DHL Paket taggleich',
  V86PARCEL: 'DHL Paket international',
  V01PAKI: 'DHL Paket (Filiale)',
  V55PAK: 'DHL Retoure',
}

interface ShipmentResult {
  shipmentTrackingNumber: string
  label: string
}

interface TrackingEvent {
  timestamp: string
  location?: string
  status: string
  description: string
}

const defaultShipper = {
  name1: '',
  streetAddress: '',
  postalCode: '',
  city: '',
  country: 'DEU',
}

export function PaketscheinPage() {
  const [step, setStep] = useState<'form' | 'result'>('form')
  const [result, setResult] = useState<ShipmentResult | null>(null)
  const [trackingInput, setTrackingInput] = useState('')
  const [showTracking, setShowTracking] = useState(false)
  const { toast } = useToast()

  const [product, setProduct] = useState<DhlProduct>('V01PAK')
  const [printFormat, setPrintFormat] = useState<'A4' | 'A6'>('A6')

  const [shipper, setShipper] = useState(defaultShipper)
  const [consignee, setConsignee] = useState({
    name1: '',
    name2: '',
    streetAddress: '',
    postalCode: '',
    city: '',
    country: 'DEU',
    email: '',
    phone: '',
  })

  const [parcel, setParcel] = useState({ weight: '1', height: '', length: '', width: '' })

  const [services, setServices] = useState({
    premium: false,
    signature: false,
    closestDropPoint: false,
    namedPersonOnly: false,
    noNeighbourDelivery: false,
  })

  const validateMutation = useMutation({
    mutationFn: (data: unknown) => api.post('/shipping/dhl/validate', data).then((r) => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (data: unknown) => api.post('/shipping/dhl/create', data).then((r) => r.data),
    onSuccess: (data: ShipmentResult) => {
      setResult(data)
      setStep('result')
      setTrackingInput(data.shipmentTrackingNumber)
      toast({ title: 'Paketschein erstellt', tone: 'success' })
    },
    onError: () => {
      toast({ title: 'Fehler beim Erstellen des Paketscheins', tone: 'danger' })
    },
  })

  const trackingQuery = useQuery<TrackingEvent[]>({
    queryKey: ['dhl-tracking', trackingInput],
    queryFn: () => api.get(`/shipping/dhl/tracking/${trackingInput}`).then((r) => r.data),
    enabled: showTracking && !!trackingInput,
    retry: false,
  })

  function buildPayload() {
    const dim =
      parcel.height && parcel.length && parcel.width
        ? { height: Number(parcel.height), length: Number(parcel.length), width: Number(parcel.width) }
        : undefined

    return {
      product,
      printFormat,
      shipper,
      consignee: {
        ...consignee,
        email: consignee.email || undefined,
        phone: consignee.phone || undefined,
      },
      parcel: { weight: Number(parcel.weight), dim },
      services,
    }
  }

  function handleValidate() {
    validateMutation.mutate(buildPayload())
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    createMutation.mutate(buildPayload())
  }

  function handleDownloadLabel() {
    if (!result?.label) return
    const link = document.createElement('a')
    link.href = `data:application/pdf;base64,${result.label}`
    link.download = `DHL_${result.shipmentTrackingNumber}.pdf`
    link.click()
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="DHL Paketschein"
        description="Paketschein erstellen und Label drucken"
        breadcrumbs={[{ label: 'Versand', to: '/versand' }, { label: 'Paketschein' }]}
      />

      {step === 'form' && (
        <form onSubmit={handleCreate} className="space-y-6">
          <Card>
            <Card.Header>
              <Card.Title className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Produkt &amp; Format
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select<DhlProduct>
                  label="DHL Produkt"
                  value={product}
                  onValueChange={(v) => setProduct(v)}
                  options={(Object.entries(productLabels) as [DhlProduct, string][]).map(
                    ([value, label]) => ({ value, label }),
                  )}
                />
                <Select<'A4' | 'A6'>
                  label="Labelformat"
                  value={printFormat}
                  onValueChange={(v) => setPrintFormat(v)}
                  options={[
                    { value: 'A6', label: 'A6 (Thermodruck)' },
                    { value: 'A4', label: 'A4 (Laserdruck)' },
                  ]}
                />
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title>Absender</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Input
                    label="Name / Firma"
                    required
                    value={shipper.name1}
                    onChange={(e) => setShipper((s) => ({ ...s, name1: e.target.value }))}
                  />
                </div>
                <Input
                  label="Strasse + Hausnr."
                  required
                  value={shipper.streetAddress}
                  onChange={(e) => setShipper((s) => ({ ...s, streetAddress: e.target.value }))}
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="PLZ"
                    required
                    value={shipper.postalCode}
                    onChange={(e) => setShipper((s) => ({ ...s, postalCode: e.target.value }))}
                  />
                  <Input
                    label="Stadt"
                    required
                    value={shipper.city}
                    onChange={(e) => setShipper((s) => ({ ...s, city: e.target.value }))}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title>Empfaenger</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Name / Firma"
                  required
                  value={consignee.name1}
                  onChange={(e) => setConsignee((c) => ({ ...c, name1: e.target.value }))}
                />
                <Input
                  label="Zusatz (optional)"
                  value={consignee.name2}
                  onChange={(e) => setConsignee((c) => ({ ...c, name2: e.target.value }))}
                />
                <Input
                  label="Strasse + Hausnr."
                  required
                  value={consignee.streetAddress}
                  onChange={(e) => setConsignee((c) => ({ ...c, streetAddress: e.target.value }))}
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="PLZ"
                    required
                    value={consignee.postalCode}
                    onChange={(e) => setConsignee((c) => ({ ...c, postalCode: e.target.value }))}
                  />
                  <Input
                    label="Stadt"
                    required
                    value={consignee.city}
                    onChange={(e) => setConsignee((c) => ({ ...c, city: e.target.value }))}
                  />
                </div>
                <Input
                  label="E-Mail (optional)"
                  type="email"
                  value={consignee.email}
                  onChange={(e) => setConsignee((c) => ({ ...c, email: e.target.value }))}
                />
                <Input
                  label="Telefon (optional)"
                  value={consignee.phone}
                  onChange={(e) => setConsignee((c) => ({ ...c, phone: e.target.value }))}
                />
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title>Paketdaten</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Input
                  label="Gewicht (kg)"
                  type="number"
                  step="0.1"
                  min="0.1"
                  required
                  value={parcel.weight}
                  onChange={(e) => setParcel((p) => ({ ...p, weight: e.target.value }))}
                />
                <Input
                  label="Laenge (cm)"
                  type="number"
                  min="1"
                  value={parcel.length}
                  onChange={(e) => setParcel((p) => ({ ...p, length: e.target.value }))}
                  placeholder="optional"
                />
                <Input
                  label="Breite (cm)"
                  type="number"
                  min="1"
                  value={parcel.width}
                  onChange={(e) => setParcel((p) => ({ ...p, width: e.target.value }))}
                  placeholder="optional"
                />
                <Input
                  label="Hoehe (cm)"
                  type="number"
                  min="1"
                  value={parcel.height}
                  onChange={(e) => setParcel((p) => ({ ...p, height: e.target.value }))}
                  placeholder="optional"
                />
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title>Zusatzleistungen</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {([
                  ['premium', 'Premium'],
                  ['signature', 'Unterschrift'],
                  ['closestDropPoint', 'Naechste Filiale'],
                  ['namedPersonOnly', 'Eigenhaendig'],
                  ['noNeighbourDelivery', 'Keine Nachbarschaftszustellung'],
                ] as const).map(([key, label]) => (
                  <Checkbox
                    key={key}
                    label={label}
                    checked={services[key]}
                    onChange={(e) => setServices((s) => ({ ...s, [key]: e.target.checked }))}
                  />
                ))}
              </div>
            </Card.Body>
          </Card>

          {validateMutation.data && (
            <Card
              className={
                validateMutation.data.valid
                  ? 'border-green-200 bg-green-50'
                  : 'border-red-200 bg-red-50'
              }
            >
              <Card.Body>
                {validateMutation.data.valid ? (
                  <p className="flex items-center gap-2 text-small font-medium text-green-800">
                    <CheckCircle2 className="w-4 h-4" /> Validierung erfolgreich
                  </p>
                ) : (
                  <div className="text-red-800">
                    <p className="flex items-center gap-2 text-small font-medium mb-1">
                      <XCircle className="w-4 h-4" /> Validierung fehlgeschlagen
                    </p>
                    <ul className="text-caption list-disc list-inside">
                      {validateMutation.data.errors.map((e: string, i: number) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={handleValidate}
              loading={validateMutation.isPending}
              disabled={validateMutation.isPending}
            >
              {validateMutation.isPending ? 'Wird geprueft…' : 'Validieren'}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={createMutation.isPending}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Wird erstellt…' : 'Paketschein erstellen'}
            </Button>
          </div>
        </form>
      )}

      {step === 'result' && result && (
        <div className="space-y-6">
          <Card className="border-green-200 bg-green-50">
            <Card.Body className="space-y-3">
              <div className="flex items-center gap-2 text-green-800 font-semibold">
                <CheckCircle2 className="w-5 h-5" />
                Paketschein erstellt!
              </div>
              <p className="text-small text-green-700">
                Sendungsnummer:{' '}
                <span className="font-mono font-bold text-body-lg">
                  {result.shipmentTrackingNumber}
                </span>
              </p>
              <div className="flex gap-3">
                {result.label && (
                  <Button
                    variant="primary"
                    leadingIcon={<Printer className="w-4 h-4" />}
                    onClick={handleDownloadLabel}
                  >
                    Label herunterladen (PDF)
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={() => {
                    setStep('form')
                    setResult(null)
                  }}
                >
                  Neuen Paketschein erstellen
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title className="flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Sendungsverfolgung
              </Card.Title>
            </Card.Header>
            <Card.Body className="space-y-4">
              <div className="flex gap-2">
                <Input
                  wrapperClassName="flex-1"
                  value={trackingInput}
                  onChange={(e) => setTrackingInput(e.target.value)}
                  placeholder="Sendungsnummer eingeben"
                />
                <Button
                  variant="primary"
                  leadingIcon={<Search className="w-4 h-4" />}
                  onClick={() => setShowTracking(true)}
                  disabled={!trackingInput}
                >
                  Verfolgen
                </Button>
              </div>

              {trackingQuery.isLoading && <LoadingState label="Tracking-Daten werden geladen…" />}

              {trackingQuery.data && trackingQuery.data.length > 0 && (
                <div className="space-y-2">
                  {trackingQuery.data.map((event, i) => (
                    <div key={i} className="flex gap-3 text-small border-l-2 border-brand-200 pl-3 py-1">
                      <span className="text-gray-400 whitespace-nowrap text-caption mt-0.5">
                        {new Date(event.timestamp).toLocaleString('de')}
                      </span>
                      <div>
                        <p className="text-gray-800 font-medium">{event.description}</p>
                        {event.location && (
                          <p className="text-gray-500 text-caption">{event.location}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {trackingQuery.data && trackingQuery.data.length === 0 && (
                <EmptyState title="Keine Tracking-Events gefunden." size="sm" />
              )}
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  )
}
